import React, { useMemo, useState } from 'react'
import { gql, useMutation, useQuery } from 'urql'
import { DateTime } from 'luxon'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { UserAvatar } from '../util/avatars'
import { useBrand } from './ui'
import { theirTime, useTimezones, zoneName } from './tz'

// GoAlert's urql client suspends by default; this dialog renders its own states.
const noSuspense = { suspense: false }

const usersQuery = gql`
  query ErecordsCoverUsers {
    users(input: { first: 100 }) {
      nodes {
        id
        name
      }
    }
  }
`
const createOverride = gql`
  mutation ErecordsCreateOverride($input: CreateUserOverrideInput!) {
    createUserOverride(input: $input) {
      id
    }
  }
`

export type CoverMode = 'add' | 'replace' | 'remove'

const DAYS = 21
const times = Array.from({ length: 48 }, (_, i) =>
  DateTime.fromObject({
    hour: Math.floor(i / 2),
    minute: (i % 2) * 30,
  }).toFormat('HH:mm'),
)

// CoverDialog is the simple way to change a schedule for a while: pick a
// person by clicking them, pick days from a list, optionally hours. It
// creates the same user override GoAlert's dialog does.
export default function CoverDialog(props: {
  scheduleID: string
  zone: string
  modes: CoverMode[]
  removeUserID?: string
  day?: string // ISO, preselects that day
  onClose: () => void
  onAdvanced?: () => void // temporary schedule
}): React.JSX.Element {
  const c = useBrand()
  const [mode, setMode] = useState<CoverMode>(props.modes[0])
  const [who, setWho] = useState('')
  const [search, setSearch] = useState('')
  const [q] = useQuery({ query: usersQuery, context: noSuspense })
  const [m, commit] = useMutation(createOverride)
  const zones = useTimezones()
  const allUsers: { id: string; name: string }[] = q.data?.users?.nodes ?? []
  const removeUser =
    allUsers.find((u) => u.id === props.removeUserID) ??
    (props.removeUserID ? { id: props.removeUserID, name: 'them' } : undefined)

  const today = DateTime.local().setZone(props.zone).startOf('day')
  const first = props.day
    ? DateTime.fromISO(props.day).setZone(props.zone).startOf('day')
    : today
  const [fromDay, setFromDay] = useState(first.toISODate() as string)
  const [toDay, setToDay] = useState(first.toISODate() as string)
  const [allDay, setAllDay] = useState(true)
  const [fromTime, setFromTime] = useState('09:00')
  const [toTime, setToTime] = useState('18:00')

  const days = useMemo(
    () => Array.from({ length: DAYS }, (_, i) => today.plus({ days: i })),
    [today.toISODate()],
  )
  const dayLabel = (d: DateTime): string =>
    d.hasSame(today, 'day')
      ? `Today, ${d.toFormat('d LLL')}`
      : d.toFormat('ccc d LLL')

  const at = (iso: string, t: string): DateTime => {
    const [h, mi] = t.split(':').map(Number)
    return DateTime.fromISO(iso, { zone: props.zone }).set({
      hour: h,
      minute: mi,
    })
  }
  let start = allDay
    ? DateTime.fromISO(fromDay, { zone: props.zone })
    : at(fromDay, fromTime)
  const end = allDay
    ? DateTime.fromISO(toDay, { zone: props.zone }).plus({ days: 1 })
    : at(toDay, toTime)
  const now = DateTime.local().startOf('minute')
  if (start < now) start = now // overrides cannot start in the past
  const valid =
    end > start &&
    (mode === 'remove' || who !== '') &&
    (mode === 'add' || Boolean(removeUser))

  const users = allUsers.filter(
    (u) =>
      u.id !== removeUser?.id &&
      u.name.toLowerCase().includes(search.toLowerCase()),
  )
  const whoName = users.find((u) => u.id === who)?.name
  const span = end.diff(start, ['days', 'hours']).toObject()
  const spanText = [
    span.days
      ? `${Math.floor(span.days)} day${Math.floor(span.days) === 1 ? '' : 's'}`
      : '',
    span.hours ? `${Math.round(span.hours)} h` : '',
  ]
    .filter(Boolean)
    .join(' ')

  const fmt = (d: DateTime): string =>
    d.setZone(props.zone).toFormat('ccc d LLL, HH:mm')
  const summary =
    mode === 'add'
      ? `${whoName ?? 'Someone'} is added to the schedule`
      : mode === 'replace'
        ? `${whoName ?? 'Someone'} covers for ${removeUser?.name}`
        : `${removeUser?.name} is off the schedule, nobody replaces them`

  const title = {
    add: 'Add someone',
    replace: `Who covers for ${removeUser?.name ?? 'them'}?`,
    remove: `Take ${removeUser?.name ?? 'them'} off`,
  }[mode]

  return (
    <Dialog open onClose={props.onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {props.modes.length > 1 && (
          <Stack direction='row' spacing={1} sx={{ mb: 2 }}>
            {props.modes.map((md) => (
              <Chip
                key={md}
                label={
                  {
                    add: 'Add someone',
                    replace: 'Someone covers',
                    remove: 'Take off',
                  }[md]
                }
                color={md === mode ? 'primary' : 'default'}
                variant={md === mode ? 'filled' : 'outlined'}
                onClick={() => setMode(md)}
              />
            ))}
          </Stack>
        )}

        {mode !== 'remove' && (
          <React.Fragment>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Who</Typography>
            {allUsers.length > 8 && (
              <TextField
                size='small'
                fullWidth
                placeholder='Search people'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 1 }}
              />
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: 1,
                mb: 2.5,
                maxHeight: 220,
                overflowY: 'auto',
              }}
            >
              {users.map((u) => {
                const sel = u.id === who
                return (
                  <Box
                    key={u.id}
                    role='button'
                    tabIndex={0}
                    onClick={() => setWho(u.id)}
                    onKeyDown={(e) => e.key === 'Enter' && setWho(u.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      border: `1.5px solid ${sel ? c.ink : c.line}`,
                      bgcolor: sel ? c.soft : 'transparent',
                      '&:hover': { bgcolor: c.hover },
                    }}
                  >
                    <UserAvatar userID={u.id} sx={{ width: 32, height: 32 }} />
                    <Typography
                      sx={{ fontSize: 14, fontWeight: sel ? 600 : 400 }}
                      noWrap
                    >
                      {u.name}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </React.Fragment>
        )}

        <Typography sx={{ fontWeight: 600, mb: 1 }}>When</Typography>
        <Stack direction='row' spacing={1} sx={{ mb: 1.5 }}>
          <TextField
            select
            size='small'
            label='From'
            value={fromDay}
            onChange={(e) => {
              setFromDay(e.target.value)
              if (e.target.value > toDay) setToDay(e.target.value)
            }}
            sx={{ flex: 1 }}
          >
            {days.map((d) => (
              <MenuItem key={d.toISODate()} value={d.toISODate() as string}>
                {dayLabel(d)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size='small'
            label='Until (including)'
            value={toDay}
            onChange={(e) => setToDay(e.target.value)}
            sx={{ flex: 1 }}
          >
            {days
              .filter((d) => (d.toISODate() as string) >= fromDay)
              .map((d) => (
                <MenuItem key={d.toISODate()} value={d.toISODate() as string}>
                  {dayLabel(d)}
                </MenuItem>
              ))}
          </TextField>
        </Stack>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
          <Chip
            label='All day'
            color={allDay ? 'primary' : 'default'}
            variant={allDay ? 'filled' : 'outlined'}
            onClick={() => setAllDay(true)}
          />
          <Chip
            label='Only some hours'
            color={!allDay ? 'primary' : 'default'}
            variant={!allDay ? 'filled' : 'outlined'}
            onClick={() => setAllDay(false)}
          />
          {!allDay && (
            <React.Fragment>
              <TextField
                select
                size='small'
                value={fromTime}
                onChange={(e) => setFromTime(e.target.value)}
                sx={{ width: 100 }}
              >
                {times.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
              <Typography sx={{ color: c.muted }}>to</Typography>
              <TextField
                select
                size='small'
                value={toTime}
                onChange={(e) => setToTime(e.target.value)}
                sx={{ width: 100 }}
              >
                {times.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </React.Fragment>
          )}
        </Stack>

        <Box
          sx={{
            p: 1.5,
            borderRadius: '12px',
            bgcolor: c.bg,
            border: `1px solid ${c.line}`,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {summary}
          </Typography>
          <Typography sx={{ fontSize: 13, color: c.muted }}>
            {end > start
              ? `${fmt(start)} → ${fmt(end)} (${spanText}) · times in ${props.zone}`
              : 'The end must be after the start.'}
          </Typography>
          {end > start &&
            [
              mode !== 'remove' && whoName ? { id: who, name: whoName } : null,
              mode !== 'add' ? removeUser : null,
            ]
              .filter((u): u is { id: string; name: string } => Boolean(u))
              .filter((u) => zones[u.id] && zones[u.id] !== props.zone)
              .map((u) => (
                <Typography
                  key={u.id}
                  sx={{ fontSize: 13, color: c.ink, mt: 0.5 }}
                >
                  For {u.name}:{' '}
                  {theirTime(
                    start.toISO() as string,
                    end.toISO() as string,
                    zones[u.id],
                  )}{' '}
                  <span style={{ color: c.muted }}>
                    ({zoneName(zones[u.id])})
                  </span>
                </Typography>
              ))}
        </Box>
        {m.error && (
          <Alert severity='error' sx={{ mt: 1.5 }}>
            {m.error.message.replace('[GraphQL] ', '')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {props.onAdvanced && (
          <Button
            size='small'
            onClick={props.onAdvanced}
            sx={{ mr: 'auto', color: c.muted }}
          >
            Temporary schedule…
          </Button>
        )}
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          variant='contained'
          disabled={!valid || m.fetching}
          onClick={() =>
            commit(
              {
                input: {
                  scheduleID: props.scheduleID,
                  start: start.toUTC().toISO(),
                  end: end.toUTC().toISO(),
                  addUserID: mode === 'remove' ? undefined : who,
                  removeUserID: mode === 'add' ? undefined : removeUser?.id,
                },
              },
              { additionalTypenames: ['UserOverride', 'Schedule'] },
            ).then((r) => {
              if (!r.error) props.onClose()
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
